import { computed, type MaybeRefOrGetter, ref, toValue } from 'vue'

import type { LocationGroupsRepository } from '../application/LocationGroupsRepository'
import type { LocationGroup, LocationGroupDraft } from '../domain/locationGroup'

export type LocationGroupsNotice = 'saved' | 'savedReloadFailed' | null

export function useLocationGroups(repository: LocationGroupsRepository, storeId: MaybeRefOrGetter<string | null>) {
  const groups = ref<readonly LocationGroup[]>([])
  const isLoading = ref(false)
  const isMutating = ref(false)
  const readError = ref<unknown>(null)
  const mutationError = ref<unknown>(null)
  const notice = ref<LocationGroupsNotice>(null)
  let requestSequence = 0
  let mutationSequence = 0
  let abortController: AbortController | null = null

  const sortedGroups = computed(() =>
    [...groups.value].sort((left, right) => left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }))
  )

  async function load(): Promise<boolean> {
    const currentStoreId = toValue(storeId)
    if (!currentStoreId) return false

    const requestId = ++requestSequence
    abortController?.abort()
    abortController = new AbortController()
    isLoading.value = true
    readError.value = null

    try {
      const result = await repository.list(currentStoreId, abortController.signal)
      if (requestId !== requestSequence || currentStoreId !== toValue(storeId)) return false
      groups.value = result
      return true
    } catch (error) {
      if (requestId !== requestSequence || abortController.signal.aborted) return false
      readError.value = error
      return false
    } finally {
      if (requestId === requestSequence) isLoading.value = false
    }
  }

  async function mutateWithResult<T>(
    action: () => Promise<T>
  ): Promise<{ successful: true; value: T } | { successful: false }> {
    if (isMutating.value) return { successful: false }
    isMutating.value = true
    const mutationId = ++mutationSequence
    const mutationStoreId = toValue(storeId)
    mutationError.value = null
    notice.value = null

    try {
      const value = await action()
      if (mutationId !== mutationSequence || mutationStoreId !== toValue(storeId)) return { successful: false }
      const reloaded = await load()
      if (mutationId !== mutationSequence) return { successful: false }
      notice.value = reloaded ? 'saved' : 'savedReloadFailed'
      return { successful: true, value }
    } catch (error) {
      if (mutationId === mutationSequence) mutationError.value = error
      return { successful: false }
    } finally {
      if (mutationId === mutationSequence) isMutating.value = false
    }
  }

  async function mutate(action: () => Promise<unknown>): Promise<boolean> {
    return (await mutateWithResult(action)).successful
  }

  function create(draft: LocationGroupDraft): Promise<boolean> {
    const currentStoreId = toValue(storeId)
    return currentStoreId ? mutate(() => repository.create(currentStoreId, draft)) : Promise.resolve(false)
  }

  async function createWithId(draft: LocationGroupDraft): Promise<string | null> {
    const currentStoreId = toValue(storeId)
    if (!currentStoreId) return null
    const result = await mutateWithResult(() => repository.create(currentStoreId, draft))
    return result.successful ? result.value : null
  }

  function update(groupId: string, draft: LocationGroupDraft): Promise<boolean> {
    const currentStoreId = toValue(storeId)
    return currentStoreId ? mutate(() => repository.update(currentStoreId, groupId, draft)) : Promise.resolve(false)
  }

  function remove(groupId: string): Promise<boolean> {
    const currentStoreId = toValue(storeId)
    return currentStoreId ? mutate(() => repository.remove(currentStoreId, groupId)) : Promise.resolve(false)
  }

  async function retryLoad(): Promise<boolean> {
    const reloaded = await load()
    if (reloaded && notice.value === 'savedReloadFailed') notice.value = 'saved'
    return reloaded
  }

  function reset(): void {
    requestSequence += 1
    mutationSequence += 1
    abortController?.abort()
    groups.value = []
    isLoading.value = false
    isMutating.value = false
    readError.value = null
    mutationError.value = null
    notice.value = null
  }

  return {
    groups: sortedGroups,
    isLoading,
    isMutating,
    readError,
    mutationError,
    notice,
    load,
    retryLoad,
    create,
    createWithId,
    update,
    remove,
    reset
  }
}
